import * as React from "react";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	IconButton,
	MenuItem,
	Paper,
	Select,
	Stack,
	Switch,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tabs,
	TextField,
	Tooltip,
	Typography,
	FormControlLabel,
	Radio,
	RadioGroup,
} from "@mui/material";
import AutoModeIcon from '@mui/icons-material/AutoMode';
import EditNoteIcon from "@mui/icons-material/EditNote";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ShareIcon from "@mui/icons-material/Share";
import HistoryIcon from "@mui/icons-material/History";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import CallMadeIcon from "@mui/icons-material/CallMade";
import PersonIcon from "@mui/icons-material/Person";
import {
	removeReportSubscription,
	updateReportSubscription,
	toggleReportSubscriptionActiveForUser,
} from "../../../services/report-subscription.service";
import { getSharedReportSubscriptions } from "../../../services/report-subscription-sharing.service";
import { dateFormat } from "../../../utilis/date-formats";
import { useDispatch, useSelector } from "react-redux";
import { ADD_NOTIFICATION } from "../../../store/types";
import NoRecordFound from "../NoRecordFound";
import SaveAsIcon from "@mui/icons-material/SaveAs";
import { AUTOMATICALLY_REPORT_TYPE } from "../../../types/report.type";
import _ from "lodash";
import CloseIcon from "@mui/icons-material/Close";
import { PAGE_TYPE } from "../../../types/pages.type";
import { isOnlyHospitalTabAccess } from "../../../utilis/common";
import ShareReportSubscriptionDialog from "../ShareReportSubscriptionDialog";
import ReportHistoryDialog from "../ReportHistoryDialog";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import useIsMobileDevice from "../../../hooks/useIsMobileDevice";

const PAGE_TABS = Object.values(PAGE_TYPE).filter(tab => tab !== "adt");

const AUTH_ROLES = {
	SUPER: 'super',
	TOTAL: 'total',
	OWNER: 'owner',
	REGIONAL: 'regional',
	ADMIN: 'admin',
	USER: 'user',
};

export default function AutomaticallyReport({
	isAutomaticReportSaved,
	page,
	isOpenAutoReport,
	type = "default",
	setIsOpenAutoReport,
	selectedSortCut,
}) {
	const isMobile = useIsMobileDevice();
	const currentUser = useSelector((state) => state.auth);
	const activeFacilities = useSelector((state) => state.activeFacilities.facilities);
	const isOnlyHospitalDashboard = React.useMemo(() => {
		return isOnlyHospitalTabAccess();
	}, []);
	const [isOpen, setIsOpen] = React.useState(false);
	const [loading, setLoading] = React.useState(true);
	const [selectedItem, setSelectedItem] = React.useState(null);
	const [data, setData] = React.useState([]);
	const [showShareDialog, setShowShareDialog] = React.useState(false);
	const [selectedReportForShare, setSelectedReportForShare] = React.useState(null);
	const [showHistoryDialog, setShowHistoryDialog] = React.useState(false);
	const [selectedReportForHistory, setSelectedReportForHistory] = React.useState(null);
	const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
	const [selectedReportForDelete, setSelectedReportForDelete] = React.useState(null);
	const [isDeleting, setIsDeleting] = React.useState(false);
	const [filterType, setFilterType] = React.useState('all'); // 'all', 'owned', 'received', 'shared'
	const [modifiedItems, setModifiedItems] = React.useState(new Map());
	const [isSaving, setIsSaving] = React.useState(false);
	const [showInactiveShared, setShowInactiveShared] = React.useState(false);
	const dispatch = useDispatch();

	const handleClose = React.useCallback(() => {
		setIsOpen(false);
		setSelectedItem(null);
		setModifiedItems(new Map()); // Clear any unsaved changes
		setIsOpenAutoReport && setIsOpenAutoReport(false);
	}, [setIsOpenAutoReport]);

	React.useEffect(() => {
		if (isOpenAutoReport) {
			setIsOpen(true);
		}
	}, [isOpenAutoReport]);

	const getAllReportSubscription = React.useCallback(async () => {
		setLoading(true);
		try {
			// Get shared reports from ALL pages which includes owned reports from all pages
			// Pass selected facilities for proper toggle display logic
			const queryParams = activeFacilities.length > 0 ? { selectedFacilities: activeFacilities } : {};
			const sharedReportsResponse = await getSharedReportSubscriptions(null, true, queryParams); // includeOwned = true, page = null

			let ownedData = [];
			let sharedReceivedData = [];

			if (sharedReportsResponse && sharedReportsResponse.status === 200) {
				const { shared = [], owned = [] } = sharedReportsResponse.data;

				// Add shared reports received from others
				sharedReceivedData = shared.map(report => ({
					...report,
					shareType: 'received',
					// Use the isActive from shareInfo for shared reports
					isActive: report.shareInfo?.isActive !== undefined ? report.shareInfo.isActive : report.isActive,
					shareInfo: report.shareInfo || { isShared: true }
				}));

				// Process owned reports to identify which ones have been shared
				ownedData = owned.map(report => {
					// Use backend-provided sharing info to determine shareType
					const hasSharedToOthers = report.shareInfo?.hasSharedToOthers || false;
					return {
						...report,
						shareType: hasSharedToOthers ? 'shared' : 'owned',
						shareInfo: {
							...report.shareInfo,
							isOwned: true,
							hasSharedToOthers
						}
					};
				});
			}

			// Combine all data
			const combinedData = [...ownedData, ...sharedReceivedData];

			setData(combinedData);
		} catch (error) {
			console.error('Error loading reports:', error);
			setData([]);
		} finally {
			setLoading(false);
		}
	}, [activeFacilities]);

	React.useEffect(() => {
		if (isOpen) {
			getAllReportSubscription();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	const handleEdit = React.useCallback((row) => {
		setSelectedItem(row);
	}, []);

	const handleShare = React.useCallback((row) => {
		setSelectedReportForShare(row);
		setShowShareDialog(true);
	}, []);

	const handleHistory = React.useCallback((row) => {
		setSelectedReportForHistory(row);
		setShowHistoryDialog(true);
	}, []);

	const handleToggleActive = React.useCallback((row, isActive) => {
		// Status is global to the report — flipping the switch applies to all
		// facilities the report covers, regardless of which are currently selected.
		setModifiedItems(prev => {
			const newMap = new Map(prev);
			const key = row.shareType === 'received' ? row.shareInfo?.shareId : row._id;

			newMap.set(key, {
				...row,
				isActive: isActive,
				originalIsActive: row.isActive
			});
			return newMap;
		});
	}, []);

	const handleSaveToggleChanges = React.useCallback(async () => {
		if (modifiedItems.size === 0) return;

		setIsSaving(true);
		try {
			const promises = Array.from(modifiedItems.values()).map(async (item) => {
				const idToUse = item.shareType === 'received' ? item.shareInfo?.shareId : item._id;
				return toggleReportSubscriptionActiveForUser(idToUse, item.isActive);
			});

			const results = await Promise.all(promises);

			const allSuccessful = results.every(result => result && result.status === 200);

			if (allSuccessful) {
				dispatch({
					type: ADD_NOTIFICATION,
					payload: {
						type: "success",
						label: `${modifiedItems.size} report subscription(s) updated successfully`,
						id: "saveToggleChanges",
					},
				});
				setModifiedItems(new Map()); // Clear modified items
				getAllReportSubscription(); // Refresh the data
			} else {
				throw new Error('Some updates failed');
			}
		} catch (error) {
			dispatch({
				type: ADD_NOTIFICATION,
				payload: {
					type: "error",
					label: error.message || "Failed to save changes",
					id: "saveToggleChangesError",
				},
			});
		} finally {
			setIsSaving(false);
		}
	}, [modifiedItems, dispatch, getAllReportSubscription]);

	// const handleDiscardChanges = React.useCallback(() => {
	// 	setModifiedItems(new Map());
	// }, []);

	const handleSave = React.useCallback(async () => {
		if (selectedItem?._id) {
			let updateData = {
				name: selectedItem?.name,
				interval: selectedItem?.interval,
			};
			await updateReportSubscription(selectedItem?._id, updateData).then((res) => {
				if (res && res.status === 200) {
					getAllReportSubscription();
					dispatch({
						type: ADD_NOTIFICATION,
						payload: {
							type: "success",
							label: `Record updated successfully`,
							id: "removeSubscription",
						},
					});
				} else {
					dispatch({
						type: ADD_NOTIFICATION,
						payload: {
							type: "error",
							label: `Something went wrong, Please try again later`,
							id: "removeSubscriptionErr",
						},
					});
				}
			});
			setSelectedItem(null);
		}
	}, [dispatch, getAllReportSubscription, selectedItem]);

	const handleDeleteClick = React.useCallback((row) => {
		setSelectedReportForDelete(row);
		setShowDeleteDialog(true);
	}, []);

	const handleDeleteConfirm = React.useCallback(async (deleteForAll) => {
		if (!selectedReportForDelete) return;

		setIsDeleting(true);
		try {
			const res = await removeReportSubscription(selectedReportForDelete._id, deleteForAll);
			if (res && res.status === 200) {
				getAllReportSubscription();
				dispatch({
					type: ADD_NOTIFICATION,
					payload: {
						type: "success",
						label: res.data?.message || `Record deleted successfully`,
						id: "removeSubscription",
					},
				});
				setShowDeleteDialog(false);
				setSelectedReportForDelete(null);
			} else {
				throw new Error(res?.data?.message || 'Failed to delete report subscription');
			}
		} catch (error) {
			dispatch({
				type: ADD_NOTIFICATION,
				payload: {
					type: "error",
					label: error.message || "Something went wrong, Please try again later",
					id: "removeSubscriptionErr",
				},
			});
		} finally {
			setIsDeleting(false);
		}
	}, [dispatch, getAllReportSubscription, selectedReportForDelete]);

	// Initialize activeTab based on the current page prop or default to first available tab
	const [activeTab, setActiveTab] = React.useState(() => {
		// If page prop matches one of the PAGE_TABS, use it; otherwise use the first tab
		return PAGE_TABS.includes(page) ? page : PAGE_TABS[0];
	});

	// Update activeTab when page prop changes
	React.useEffect(() => {
		if (PAGE_TABS.includes(page)) {
			setActiveTab(page);
		}
	}, [page]);

	// Filter data based on selected tab (page field) and filter type
	const filteredData = React.useMemo(() => {
		let pageFilteredData = data.filter(row => row.page === activeTab);

		switch (filterType) {
			case 'owned':
				return pageFilteredData.filter(row => row.shareType === 'owned');
			case 'received':
				return pageFilteredData.filter(row => {
					if (row.shareType !== 'received') return false;
					if (showInactiveShared) return true;
					return row.isActive !== false;
				});
			case 'shared':
				return pageFilteredData.filter(row => row.shareType === 'shared');
			case 'all':
			default:
				return pageFilteredData;
		}
	}, [data, activeTab, filterType, showInactiveShared]);

	if (isAutomaticReportSaved) {
		return (
			<>
				{selectedSortCut?.isAutoReport && !isMobile && (
					<Box>
						<Tooltip title={"Automated Reports"} arrow>
							<IconButton
								onClick={() => setIsOpen(true)}
								size="small"
								disableFocusRipple={false}
								disableRipple={false}
								sx={{
									width: 30,
									height: 30,
									ml: 1,
									borderRadius: "4px",
									'&:hover': {
										backgroundColor: '#4879f5',
										color: "white",
									},
								}}
							>
								<AutoModeIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</Box>
				)}
				{isOpen && (
					<Dialog fullWidth={true} fullScreen={isMobile} maxWidth={"lg"} open={isOpen} onClose={handleClose}>
						<DialogTitle sx={{ 
							display: 'flex', 
							alignItems: 'center', 
							justifyContent: 'space-between',
							px: { xs: 2, sm: 3 }
						}}>
							<Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
								Automated Reports list
							</Typography>
							{isMobile && (
								<IconButton
									edge="end"
									color="inherit"
									onClick={handleClose}
									aria-label="close"
								>
									<CloseIcon />
								</IconButton>
							)}
						</DialogTitle>
						<DialogContent sx={{ px: 1, pb: 1 }}>
							{!isOnlyHospitalDashboard && (
								<>
									<Tabs
										value={activeTab}
										onChange={(event, newValue) => setActiveTab(newValue)}
										indicatorColor="primary"
										textColor="primary"
										variant="scrollable"
										scrollButtons="auto"
										sx={{
											"& .MuiTabs-indicator": {
												backgroundColor: "#1976D2", // Custom active indicator color
												height: "4px", // Thicker underline
												borderRadius: "2px", // Rounded edges
											},
											"& .MuiTab-root": {
												textTransform: "capitalize", // Makes tab labels look clean
												fontWeight: "bold", // Bold font
												fontSize: "14px",
												color: "#555", // Default color
												transition: "0.3s",
												"&:hover": {
													color: "#1976D2", // Blue on hover
												},
											},
											"& .Mui-selected": {
												color: "#1976D2 !important", // Selected tab color
											},
										}}
									>
										{PAGE_TABS.map((pageType) => (
											<Tab
												key={pageType}
												label={pageType.replace(/([A-Z])/g, " $1")} // Adds space between words
												value={pageType}
												sx={{
													borderRadius: "8px", // Slight rounded corners
													padding: "6px 12px",
													minWidth: "100px", // Minimum tab width
													"&.Mui-selected": {
														backgroundColor: "#E3F2FD", // Light blue background for selected tab
													},
												}}
											/>
										))}
									</Tabs>
									<Divider />
								</>
							)}

							{/* Filter Section */}
							<Box sx={{ mb: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid rgba(0,0,0,0.1)' }}>
								<Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#333' }}>
									Filter Reports
								</Typography>
								<RadioGroup
									row={!isMobile}
									value={filterType}
									onChange={(e) => setFilterType(e.target.value)}
									sx={{ 
										gap: isMobile ? 1 : 3,
										display: 'flex',
										flexDirection: isMobile ? 'column' : 'row'
									}}
								>
									<FormControlLabel
										value="all"
										control={<Radio size="small" />}
										label={
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
												<Typography variant="body2">Show All</Typography>
												<Chip
													label={data.filter(row => row.page === activeTab).length}
													size="small"
													sx={{ height: 20, fontSize: '0.75rem', ml: 0.5 }}
												/>
											</Box>
										}
									/>
									<FormControlLabel
										value="owned"
										control={<Radio size="small" />}
										label={
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
												<PersonIcon sx={{ fontSize: 16, color: '#2196f3' }} />
												<Typography variant="body2">My Items</Typography>
												<Chip
													label={data.filter(row => row.page === activeTab && row.shareType === 'owned').length}
													size="small"
													sx={{ height: 20, fontSize: '0.75rem', ml: 0.5 }}
												/>
											</Box>
										}
									/>
									<FormControlLabel
										value="received"
										control={<Radio size="small" />}
										label={
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
												<CallReceivedIcon sx={{ fontSize: 16, color: '#4caf50' }} />
												<Typography variant="body2">Shared with Me</Typography>
												<Chip
													label={data.filter(row => row.page === activeTab && row.shareType === 'received').length}
													size="small"
													sx={{ height: 20, fontSize: '0.75rem', ml: 0.5 }}
												/>
											</Box>
										}
									/>
									<FormControlLabel
										value="shared"
										control={<Radio size="small" />}
										label={
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
												<CallMadeIcon sx={{ fontSize: 16, color: '#ff9800' }} />
												<Typography variant="body2">Items I've Shared</Typography>
												<Chip
													label={data.filter(row => row.page === activeTab && row.shareType === 'shared').length}
													size="small"
													sx={{ height: 20, fontSize: '0.75rem', ml: 0.5 }}
												/>
											</Box>
										}
									/>
								</RadioGroup>
								{/* Show inactive shared reports toggle */}
								{filterType === 'received' && (
									<FormControlLabel
										control={
											<Switch
												checked={showInactiveShared}
												onChange={e => setShowInactiveShared(e.target.checked)}
												size="small"
												color="primary"
											/>
										}
										label={<Typography variant="body2">Show inactive shared reports</Typography>}
										sx={{ mt: 1, ml: 1 }}
									/>
								)}
							</Box>
							{isMobile ? (
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
									{loading && (
										<Box display={"flex"} justifyContent={"center"} sx={{ mt: 2, py: 4 }}>
											<CircularProgress />
										</Box>
									)}
									{!loading && filteredData.length > 0 && filteredData.map((row) => {
										const ID = row?._id;
										const isEdit = selectedItem?._id === ID;
										const facilitiesNames = row?.facilitiesNames || [];
										const modifiedKey = row.shareType === 'received' ? row.shareInfo?.shareId : row._id;
										const modifiedRow = modifiedItems.get(modifiedKey);
										const displayRow = modifiedRow || row;
										const isModified = modifiedItems.has(modifiedKey);
										const displayToggleState = displayRow.isActive !== false;

										return (
											<Paper
												key={row._id || row.name}
												elevation={1}
												sx={{
													p: 2,
													borderRadius: 2,
													border: isModified ? '1px solid #ff9800' : '1px solid #e0e0e0',
													borderLeft: isModified ? '4px solid #ff9800' : '1px solid #e0e0e0',
													backgroundColor: isModified ? 'rgba(255, 152, 0, 0.04)' : 'inherit',
													position: 'relative'
												}}
											>
												{/* Header with Switch and Type Badge */}
												<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
													<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
														<Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
															Status:
														</Typography>
														<Switch
															checked={displayToggleState}
															onChange={(e) => handleToggleActive(row, e.target.checked)}
															color="primary"
															size="small"
															disabled={isEdit}
														/>
														{isModified && (
															<Box
																sx={{
																	width: 8,
																	height: 8,
																	borderRadius: '50%',
																	backgroundColor: '#ff9800',
																	animation: 'pulse 1.5s infinite',
																	'@keyframes pulse': {
																		'0%': { opacity: 1 },
																		'50%': { opacity: 0.5 },
																		'100%': { opacity: 1 }
																	}
																}}
															/>
														)}
													</Box>
													
													<Box>
														{row.shareType === 'received' && (
															<Chip
																icon={<CallReceivedIcon sx={{ fontSize: '12px !important' }} />}
																label="Received"
																size="small"
																sx={{
																	backgroundColor: 'rgba(76, 175, 80, 0.1)',
																	color: '#4caf50',
																	border: '1px solid rgba(76, 175, 80, 0.3)',
																	fontSize: '0.7rem',
																	height: '20px',
																	'& .MuiChip-icon': { color: '#4caf50' }
																}}
															/>
														)}
														{row.shareType === 'shared' && (
															<Chip
																icon={<CallMadeIcon sx={{ fontSize: '12px !important' }} />}
																label="Shared"
																size="small"
																sx={{
																	backgroundColor: 'rgba(255, 152, 0, 0.1)',
																	color: '#ff9800',
																	border: '1px solid rgba(255, 152, 0, 0.3)',
																	fontSize: '0.7rem',
																	height: '20px',
																	'& .MuiChip-icon': { color: '#ff9800' }
																}}
															/>
														)}
														{row.shareType === 'owned' && (
															<Chip
																icon={<PersonIcon sx={{ fontSize: '12px !important' }} />}
																label="Mine"
																size="small"
																sx={{
																	backgroundColor: 'rgba(33, 150, 243, 0.1)',
																	color: '#2196f3',
																	border: '1px solid rgba(33, 150, 243, 0.3)',
																	fontSize: '0.7rem',
																	height: '20px',
																	'& .MuiChip-icon': { color: '#2196f3' }
																}}
															/>
														)}
													</Box>
												</Box>

												<Divider sx={{ mb: 1.5 }} />

												{/* Body Information */}
												<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
													<Box>
														<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
															Report Name
														</Typography>
														{isEdit ? (
															<TextField
																fullWidth
																size="small"
																value={selectedItem?.name}
																onChange={(e) =>
																	setSelectedItem((prevState) => ({
																		...prevState,
																		name: e.target.value,
																	}))
																}
																sx={{ mt: 0.5 }}
															/>
														) : (
															<Typography variant="body2" sx={{ fontWeight: 'bold', color: '#111827' }}>
																{row?.shareInfo?.newName || row?.name}
															</Typography>
														)}
														{row.shareType === 'received' && row.shareInfo?.sharedBy && (
															<Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.5 }}>
																Shared by: {row.shareInfo.sharedBy.fullName}
															</Typography>
														)}
													</Box>

													<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1 }}>
														<Box>
															<Typography variant="caption" color="text.secondary">
																Facility
															</Typography>
															<Box sx={{ mt: 0.5 }}>
																{facilitiesNames.length > 0 ? (
																	<Chip
																		label={
																			<Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
																				<Typography
																					sx={{
																						fontFamily: "Manrope",
																						fontWeight: 600,
																						fontSize: "10px",
																						whiteSpace: "nowrap",
																						overflow: "hidden",
																						textOverflow: "ellipsis",
																						maxWidth: "60px",
																					}}
																				>
																					{facilitiesNames[0]}
																				</Typography>
																				{facilitiesNames.length > 1 && (
																					<Box
																						sx={{
																							backgroundColor: "#1976D2",
																							color: "white",
																							borderRadius: "8px",
																							padding: "1px 4px",
																							fontSize: "9px",
																							fontWeight: "bold",
																						}}
																					>
																						+{facilitiesNames.length - 1}
																					</Box>
																				)}
																			</Box>
																		}
																		size="small"
																	/>
																) : (
																	<Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic' }}>
																		No facilities
																	</Typography>
																)}
															</Box>
														</Box>

														<Box>
															<Typography variant="caption" color="text.secondary">
																Interval
															</Typography>
															<Box sx={{ mt: 0.5 }}>
																{isEdit ? (
																	<FormControl fullWidth size="small">
																		<Select
																			value={selectedItem?.interval}
																			onChange={(e) =>
																				setSelectedItem((prevState) => ({
																					...prevState,
																					interval: e.target.value,
																				}))
																			}
																			sx={{ height: 32, fontSize: '0.85rem' }}
																		>
																			<MenuItem value={AUTOMATICALLY_REPORT_TYPE.DAILY}>
																				{_.capitalize(AUTOMATICALLY_REPORT_TYPE.DAILY)}
																			</MenuItem>
																			<MenuItem value={AUTOMATICALLY_REPORT_TYPE.MONTHLY}>
																				{_.capitalize(AUTOMATICALLY_REPORT_TYPE.MONTHLY)}
																			</MenuItem>
																			<MenuItem value={AUTOMATICALLY_REPORT_TYPE.YEARLY}>
																				{_.capitalize(AUTOMATICALLY_REPORT_TYPE.YEARLY)}
																			</MenuItem>
																		</Select>
																	</FormControl>
																) : (
																	<Chip 
																		label={_.capitalize(row.interval)} 
																		size="small" 
																		variant="outlined"
																		sx={{ fontSize: '0.75rem', height: 24 }}
																	/>
																)}
															</Box>
														</Box>
													</Box>

													<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1 }}>
														<Box>
															<Typography variant="caption" color="text.secondary">
																Report / Card Title
															</Typography>
															<Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.8rem', color: '#4B5563' }}>
																{row?.title} {row?.adtType != null && "(ADT)"}
															</Typography>
														</Box>

														<Box>
															<Typography variant="caption" color="text.secondary">
																Register Date
															</Typography>
															<Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.8rem', color: '#4B5563' }}>
																{dateFormat(row.createdAt)}
															</Typography>
														</Box>
													</Box>
												</Box>

												<Divider sx={{ my: 1.5 }} />

												{/* Action Buttons */}
												<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
													{isEdit ? (
														<>
															<Button
																size="small"
																variant="contained"
																color="primary"
																onClick={handleSave}
																startIcon={<SaveAsIcon sx={{ fontSize: '16px !important' }} />}
																sx={{ fontSize: '0.75rem', py: 0.5 }}
															>
																Save
															</Button>
															<Button
																size="small"
																variant="outlined"
																color="inherit"
																onClick={() => setSelectedItem(null)}
																startIcon={<CloseIcon sx={{ fontSize: '16px !important' }} />}
																sx={{ fontSize: '0.75rem', py: 0.5 }}
															>
																Cancel
															</Button>
															{row.shareType !== 'received' && (
																<Button
																	size="small"
																	variant="outlined"
																	color="error"
																	onClick={() => handleDeleteClick(selectedItem || row)}
																	startIcon={<DeleteOutlineOutlinedIcon sx={{ fontSize: '16px !important' }} />}
																	sx={{ fontSize: '0.75rem', py: 0.5 }}
																>
																	Delete
																</Button>
															)}
														</>
													) : (
														<>
															{row.shareType !== 'received' && (
																<>
																	<Button
																		size="small"
																		variant="outlined"
																		color="primary"
																		onClick={() => handleEdit(row)}
																		startIcon={<EditNoteIcon sx={{ fontSize: '16px !important' }} />}
																		sx={{ fontSize: '0.75rem', minWidth: 0, px: 1.5 }}
																	>
																		Edit
																	</Button>
																	{currentUser?.role?.slug !== AUTH_ROLES.USER && (
																		<Button
																			size="small"
																			variant="outlined"
																			color="info"
																			onClick={() => handleShare(row)}
																			startIcon={<ShareIcon sx={{ fontSize: '16px !important' }} />}
																			sx={{ fontSize: '0.75rem', minWidth: 0, px: 1.5 }}
																		>
																			Share
																		</Button>
																	)}
																	<Button
																		size="small"
																		variant="outlined"
																		color="secondary"
																		onClick={() => handleHistory(row)}
																		startIcon={<HistoryIcon sx={{ fontSize: '16px !important' }} />}
																		sx={{ fontSize: '0.75rem', minWidth: 0, px: 1.5 }}
																	>
																		History
																	</Button>
																</>
															)}
															{row.shareType === 'received' && (
																<>
																	<Button
																		size="small"
																		variant="outlined"
																		color="secondary"
																		onClick={() => handleHistory(row)}
																		startIcon={<HistoryIcon sx={{ fontSize: '16px !important' }} />}
																		sx={{ fontSize: '0.75rem', minWidth: 0, px: 1.5 }}
																	>
																		History
																	</Button>
																	<Button
																		size="small"
																		variant="outlined"
																		color="error"
																		onClick={() => handleDeleteClick(row)}
																		startIcon={<DeleteOutlineOutlinedIcon sx={{ fontSize: '16px !important' }} />}
																		sx={{ fontSize: '0.75rem', minWidth: 0, px: 1.5 }}
																	>
																		Remove
																	</Button>
																</>
															)}
														</>
													)}
												</Box>
											</Paper>
										);
									})}
									{!loading && filteredData.length === 0 && (
										<Paper sx={{ p: 4, textAlign: 'center', border: '1px dashed #ccc' }}>
											<NoRecordFound data={filteredData} />
										</Paper>
									)}
								</Box>
							) : (
								<TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
									<Table sx={{ minWidth: 800 }} aria-label="simple table" size="small">
										<TableHead>
											<TableRow>
												<TableCell align="center">Status</TableCell>
												<TableCell align="center">Type</TableCell>
												<TableCell>Report name</TableCell>
												<TableCell >Facility</TableCell>
												<TableCell align="center">Report Type</TableCell>
												<TableCell align="center">Card Title</TableCell>
												<TableCell align="center">Report register date</TableCell>
												<TableCell align="left">Actions</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{loading && (
												<TableRow key={"loading"}>
													<TableCell colSpan={8}>
														<Box display={"flex"} justifyContent={"center"} sx={{ mt: 1, height: "50px" }}>
															<CircularProgress />
														</Box>
													</TableCell>
												</TableRow>
											)}
											{!loading &&
												filteredData.length > 0 &&
												filteredData.map((row) => {
													const ID = row?._id;
													const isEdit = selectedItem?._id === ID;
													const facilitiesNames = row?.facilitiesNames || [];
													// Use correct key for checking modified items
													const modifiedKey = row.shareType === 'received' ? row.shareInfo?.shareId : row._id;
													const modifiedRow = modifiedItems.get(modifiedKey);
													const displayRow = modifiedRow || row;
													const isModified = modifiedItems.has(modifiedKey);
													
													const displayToggleState = displayRow.isActive !== false;

													return (
														<TableRow
															key={row._id || row.name}
															sx={{
																"&:last-child td, &:last-child th": { border: 0 },
																backgroundColor: isModified ? 'rgba(255, 152, 0, 0.08)' : 'inherit',
																borderLeft: isModified ? '3px solid #ff9800' : 'none',
															}}
															style={{
																fontSize: isMobile ? '12px' : 'inherit',
															}}
														>
															<TableCell align="center">
																<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
																	<Tooltip title={displayToggleState ? "Deactivate report" : "Activate report"}>
																		<Switch
																			checked={displayToggleState}
																			onChange={(e) => handleToggleActive(row, e.target.checked)}
																			color="primary"
																			size="small"
																			disabled={isEdit}
																		/>
																	</Tooltip>
																	{isModified && (
																		<Tooltip title="Modified - remember to save changes">
																			<Box
																				sx={{
																					width: 8,
																					height: 8,
																					borderRadius: '50%',
																					backgroundColor: '#ff9800',
																					animation: 'pulse 1.5s infinite',
																					'@keyframes pulse': {
																						'0%': { opacity: 1 },
																						'50%': { opacity: 0.5 },
																						'100%': { opacity: 1 }
																					}
																				}}
																			/>
																		</Tooltip>
																	)}
																</Box>
															</TableCell>
															<TableCell align="center">
																{row.shareType === 'received' && (
																	<Tooltip title={`Shared with you by ${row.shareInfo?.sharedBy?.fullName || 'Unknown'}`}>
																		<Chip
																			icon={<CallReceivedIcon sx={{ fontSize: '14px !important' }} />}
																			label="Received"
																			size="small"
																			sx={{
																				backgroundColor: 'rgba(76, 175, 80, 0.1)',
																				color: '#4caf50',
																				border: '1px solid rgba(76, 175, 80, 0.3)',
																				fontSize: '0.75rem',
																				height: '24px',
																				'& .MuiChip-icon': {
																					color: '#4caf50',
																				}
																			}}
																		/>
																	</Tooltip>
																)}
																{row.shareType === 'shared' && (
																	<Tooltip title="You have shared this report with others">
																		<Chip
																			icon={<CallMadeIcon sx={{ fontSize: '14px !important' }} />}
																			label="Shared"
																			size="small"
																			sx={{
																				backgroundColor: 'rgba(255, 152, 0, 0.1)',
																				color: '#ff9800',
																				border: '1px solid rgba(255, 152, 0, 0.3)',
																				fontSize: '0.75rem',
																				height: '24px',
																				'& .MuiChip-icon': {
																					color: '#ff9800',
																				}
																			}}
																		/>
																	</Tooltip>
																)}
																{row.shareType === 'owned' && (
																	<Tooltip title="Your personal report">
																		<Chip
																			icon={<PersonIcon sx={{ fontSize: '14px !important' }} />}
																			label="Mine"
																			size="small"
																			sx={{
																				backgroundColor: 'rgba(33, 150, 243, 0.1)',
																				color: '#2196f3',
																				border: '1px solid rgba(33, 150, 243, 0.3)',
																				fontSize: '0.75rem',
																				height: '24px',
																				'& .MuiChip-icon': {
																					color: '#2196f3',
																				}
																			}}
																		/>
																	</Tooltip>
																)}
															</TableCell>
															<TableCell component="th" scope="row">
																{isEdit ? (
																	<TextField
																		size="small"
																		label="Report Name"
																		value={selectedItem?.name}
																		onChange={(e) =>
																			setSelectedItem((prevState) => ({
																				...prevState,
																				name: e.target.value,
																			}))
																		}
																	/>
																) : (
																	<Box>
																		<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
																			{row?.shareInfo?.newName || row?.name}
																		</Typography>
																		{/* {row?.description && (
																			<Typography variant="caption" sx={{ color: '#666', display: 'block', fontStyle: 'italic', mt: 0.5 }}>
																				{row.description}
																			</Typography>
																		)} */}
																		{row.shareType === 'received' && row.shareInfo?.sharedBy && (
																			<Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
																				Shared by: {row.shareInfo.sharedBy.fullName}
																			</Typography>
																		)}
																		{row.shareType === 'shared' && (
																			<Typography variant="caption" sx={{ color: '#ff9800', display: 'block' }}>
																				Shared with others
																			</Typography>
																		)}
																	</Box>
																)}

															</TableCell>
															<TableCell component="th" scope="row">
																<Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
																	{facilitiesNames.length > 0 ? (
																		<Tooltip title={facilitiesNames.join(", ")}>
																			<Chip
																				label={
																					<Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
																						<Typography
																							sx={{
																								fontFamily: "Manrope",
																								fontWeight: 600,
																								fontSize: "10px",
																								whiteSpace: "nowrap",
																								overflow: "hidden",
																								textOverflow: "ellipsis",
																								maxWidth: "40px",
																							}}
																						>
																							{facilitiesNames[0]} {/* First facility name */}
																						</Typography>

																						{facilitiesNames.length > 1 && (
																							<Box
																								sx={{
																									backgroundColor: "#1976D2", // Blue background for count
																									color: "white",
																									borderRadius: "8px",
																									padding: "2px 6px",
																									fontSize: "10px",
																									fontWeight: "bold",
																									minWidth: "20px",
																									textAlign: "center",
																								}}
																							>
																								+{facilitiesNames.length - 1} {/* Unique styled count */}
																							</Box>
																						)}
																					</Box>
																				}
																				size="small"
																				sx={{ padding: "2px 6px" }}
																			/>
																		</Tooltip>
																	) : (
																		<Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
																			No facilities
																		</Typography>
																	)}
																</Box>



															</TableCell>
															{isEdit ? (
																<TableCell>
																	<FormControl fullWidth>
																		<Select
																			labelId="interval-simple-select-label"
																			id="interval-simple-select"
																			size="small"
																			value={selectedItem?.interval}
																			onChange={(e) =>
																				setSelectedItem((prevState) => ({
																					...prevState,
																					interval: e.target.value,
																				}))
																			}
																		>
																			<MenuItem value={AUTOMATICALLY_REPORT_TYPE.DAILY}>
																				{_.capitalize(AUTOMATICALLY_REPORT_TYPE.DAILY)}
																			</MenuItem>
																			<MenuItem value={AUTOMATICALLY_REPORT_TYPE.MONTHLY}>
																				{_.capitalize(AUTOMATICALLY_REPORT_TYPE.MONTHLY)}
																			</MenuItem>
																			<MenuItem value={AUTOMATICALLY_REPORT_TYPE.YEARLY}>
																				{_.capitalize(AUTOMATICALLY_REPORT_TYPE.YEARLY)}
																			</MenuItem>
																		</Select>
																	</FormControl>
																</TableCell>
															) : (
																<TableCell align="center">{_.capitalize(row.interval)}</TableCell>
															)}
															<TableCell align="center">
																{row?.title} {row?.adtType != null && "(ADT)"}
															</TableCell>
															<TableCell align="center">{dateFormat(row.createdAt)}</TableCell>
															<TableCell align="left">
																<Stack spacing={1} direction={"row"} justifyContent={"flex-start"} sx={{ flexWrap: 'nowrap' }}>
																	{isEdit ? (
																		<>
																			<Tooltip title="Update">
																				<SaveAsIcon sx={{ cursor: "pointer" }} color="primary" onClick={handleSave} />
																			</Tooltip>
																			<Tooltip title="Cancel">
																				<CloseIcon
																					sx={{ cursor: "pointer" }}
																					color="primary"
																					onClick={() => setSelectedItem(null)}
																				/>
																			</Tooltip>
																			{row.shareType !== 'received' && (
																				<Tooltip title="Delete">
																					<DeleteOutlineOutlinedIcon
																						onClick={() => handleDeleteClick(selectedItem || row)}
																						sx={{ cursor: "pointer" }}
																						color="error"
																					/>
																				</Tooltip>
																			)}
																		</>
																	) : (
																		<>
																			{/* Only allow editing/deleting if it's owned by the user (not received) and user is the creator */}
																			{row.shareType !== 'received' && (
																				<>
																					<Tooltip title="Edit">
																						<EditNoteIcon
																							onClick={() => handleEdit(row)}
																							sx={{ cursor: "pointer" }}
																							color="primary"
																						/>
																					</Tooltip>
																					{/* Hide Share button for USER role */}
																					{currentUser?.role?.slug !== AUTH_ROLES.USER && (
																						<Tooltip title="Share">
																							<ShareIcon
																								onClick={() => handleShare(row)}
																								sx={{ cursor: "pointer" }}
																								color="info"
																							/>
																						</Tooltip>
																					)}
																					<Tooltip title="History">
																						<HistoryIcon
																							onClick={() => handleHistory(row)}
																							sx={{ cursor: "pointer" }}
																							color="secondary"
																						/>
																					</Tooltip>
																				</>
																			)}
																			{/* For received reports, show history and delete actions */}
																			{row.shareType === 'received' && (
																				<>
																					<Tooltip title="History">
																						<HistoryIcon
																							onClick={() => handleHistory(row)}
																							sx={{ cursor: "pointer" }}
																							color="secondary"
																						/>
																					</Tooltip>
																					<Tooltip title="Remove from my list">
																						<DeleteOutlineOutlinedIcon
																							onClick={() => handleDeleteClick(row)}
																							sx={{ cursor: "pointer" }}
																							color="error"
																						/>
																					</Tooltip>
																				</>
																			)}
																		</>
																	)}
																</Stack>
															</TableCell>
														</TableRow>
													);
												})}
										</TableBody>
										{filteredData.length === 0 && !loading && (
											<TableRow>
												<TableCell colSpan={8} align="center">
													<NoRecordFound data={filteredData} />
												</TableCell>
											</TableRow>
										)}
									</Table>
								</TableContainer>
							)}
						</DialogContent>
						{(!isMobile || modifiedItems.size > 0) && (
							<DialogActions>
								{modifiedItems.size > 0 && (
									<Button
										variant="contained"
										onClick={handleSaveToggleChanges}
										disabled={isSaving}
										startIcon={isSaving ? <CircularProgress size={16} /> : <SaveAsIcon />}
										sx={{ mr: 'auto' }}
									>
										{isSaving ? 'Saving...' : `Save Changes (${modifiedItems.size})`}
									</Button>
								)}
								{!isMobile && <Button onClick={handleClose}>Close</Button>}
							</DialogActions>
						)}
					</Dialog>
				)}
				{showShareDialog && selectedReportForShare && (
					<ShareReportSubscriptionDialog
						reportSubscription={selectedReportForShare}
						onClose={() => {
							setShowShareDialog(false);
							setSelectedReportForShare(null);
						}}
						onShareSuccess={() => {
							setShowShareDialog(false);
							setSelectedReportForShare(null);
							// Optionally refresh the report data
							getAllReportSubscription();
						}}
					/>
				)}
				{showHistoryDialog && selectedReportForHistory && (
					<ReportHistoryDialog
						open={showHistoryDialog}
						reportId={selectedReportForHistory._id}
						reportName={selectedReportForHistory.name}
						onClose={() => {
							setShowHistoryDialog(false);
							setSelectedReportForHistory(null);
						}}
					/>
				)}
				{showDeleteDialog && selectedReportForDelete && (
					<DeleteConfirmationDialog
						open={showDeleteDialog}
						report={selectedReportForDelete}
						isLoading={isDeleting}
						onClose={() => {
							setShowDeleteDialog(false);
							setSelectedReportForDelete(null);
						}}
						onConfirm={handleDeleteConfirm}
					/>
				)}
			</>
		);
	}

	return null;
}
